import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "@components/ui/Logo";
import arrowDownIcon from "@assets/images/arrow-down-s-line1.svg";
import dropdownBadge from "@assets/images/Group-1410087795.svg";
import useOutsideClick from "@/hooks/useOutsideClick";
import { navLinks } from "../data";

const userTypes = [
  { label: "Personal", href: "/residential", id: "personal" },
  { label: "Business", href: "/business", id: "business" },
  { label: "Partnership", href: "/partners", id: "partnership" },
] as const;
const PartnerHeader = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const closeDropdown = useCallback(() => setOpenDropdown(null), []);
  const dropdownRef = useOutsideClick(closeDropdown);

  // Close menus on route change
  useEffect(() => {
    setOpenDropdown(null);
    setMobileMenuOpen(false);
    setMobileExpanded(null);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.classList.toggle("overflow-hidden", mobileMenuOpen);
    return () => document.body.classList.remove("overflow-hidden");
  }, [mobileMenuOpen]);

  const currentUserType = useMemo(() => {
    if (location.pathname.startsWith("/business")) return "business";
    if (location.pathname.startsWith("/partners")) return "partnership";
    return "personal";
  }, [location.pathname]);
  // Check if a nav item or any of its children match the current route
  const isNavActive = useCallback(
    (item: (typeof navLinks)[0]) => 
      item.links.some((link) => location.pathname === link.href),
    [location.pathname]
  );

  const toggleDropdown = useCallback((title: string) => {
    setOpenDropdown((prev) => prev === title ? null : title);
  }, []);

  const toggleMobileExpanded = useCallback((title: string) => {
    setMobileExpanded((prev) => prev === title ? null : title);
  }, []);

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  return (
    <header className="w-full">
      <nav className="w-full flex items-center justify-between gap-4 xl:gap-6 2xl:gap-10 h-[68px] md:h-[80px]">
        {/* Left side - Logo and User Type Switcher */}
        <div className="flex items-center gap-3 md:gap-4 xl:gap-5">
          <Logo variant="white" />

          {/* User Type Switcher - Desktop (pill style) */}
          <div className="hidden md:flex items-center bg-white/10 rounded-full p-[3px] backdrop-blur-sm">
            {userTypes.map((type) => (
              <Link
                key={type.id}
                to={type.href}
                className={`px-2.5 xl:px-4 py-1 xl:py-1.5 rounded-full text-[11px] md:text-[12px] xl:text-[14px] tracking-[-0.02em] font-medium transition-all duration-200 whitespace-nowrap ${
                  currentUserType === type.id
                    ? "bg-white text-[#122d63] shadow-sm"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
              >
                {type.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Right side - Navigation and Login (xl+ only) */}
        <div className="hidden xl:flex items-center gap-3 2xl:gap-5">
          {/* Navigation Links */}
          <div ref={dropdownRef} className="flex items-center gap-0 2xl:gap-1">
            {navLinks.map((item) => {
              const active = isNavActive(item);
              const isOpen = openDropdown === item.title;
              const isCompanyDropdown = item.title === "Company";
              
              return (
                <div key={item.title} className="relative">
                  <button
                    onClick={() => toggleDropdown(item.title)}
                    className={`flex items-center gap-0 whitespace-nowrap px-1.5 2xl:px-2.5 py-1.5 rounded-full text-[14px] 2xl:text-[16px] transition-all duration-200 ${
                      active
                        ? "text-white bg-white/15"
                        : "text-[#f8f8f8] hover:text-white hover:bg-white/10"
                    }`}
                  >
                    {item.title}
                    <img
                      src={arrowDownIcon}
                      alt=""
                      className={`w-4 h-4 2xl:w-5 2xl:h-5 transition-transform duration-200 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* Desktop Dropdown */}
                  {isOpen && (
                    <div
                      className={`absolute top-full pt-3 -translate-x-1/2 ${
                        isCompanyDropdown ? "-left-1/2" : "left-1/2"
                      }`}
                    >
                      {/* Pointer */}
                      <svg
                        width="20"
                        height="10"
                        viewBox="0 0 20 10"
                        className={`absolute -top-[3px] z-10 -translate-x-1/2 ${
                          isCompanyDropdown ? "left-[70%]" : "left-1/2"
                        }`}
                      >
                        <path d="M0 10L10 0L20 10H0Z" fill="white" />
                      </svg>

                      <div className="flex bg-white shadow shadow-black/30 rounded-[12px] overflow-hidden">
                        {/* Links */}
                        <div className="p-6 2xl:p-10 w-[260px] 2xl:w-[300px]">
                          <h3 className="text-xs text-[#0A0E2EBF] font-semibold font-albert-sans">
                            {item.title}
                          </h3>
                          <div className="flex flex-col gap-3 mt-3">
                            {item.links.map((link) => {
                              const childActive = location.pathname === link.href;
                              return (
                                <Link
                                  key={link.href}
                                  to={link.href}
                                  onClick={closeDropdown}
                                  className={`text-[13px] 2xl:text-[14px] leading-tight w-fit font-albert-sans transition-colors ${
                                    childActive
                                      ? "text-[#f24d2b] font-semibold"
                                      : "text-[#0A0E2E] hover:text-[#f24d2b] hover:underline"
                                  }`}
                                >
                                  {link.title}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                        {/* Badge illustration */}
                        <div className="w-[180px] 2xl:w-[212px] bg-[#F1F1F1] p-5 grid place-content-center">
                          <img
                            src={item.icon || dropdownBadge}
                            alt=""
                            className="w-[80px] 2xl:w-[100px] h-auto object-contain"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Login Button */}
          <Link
            to="/login"
            className="py-2 2xl:py-2.5 px-4 2xl:px-5 bg-[#f8f8f8] rounded-[40px] text-sm 2xl:text-base text-[#0a0e2e] hover:bg-[#dedede] transition-colors whitespace-nowrap"
          >
            Login
          </Link>
        </div>

        {/* Mobile/Tablet Menu Button (below xl) */}
        <button
          className="xl:hidden flex flex-col items-center justify-center w-10 h-10 gap-1.5"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span
            className={`w-6 h-0.5 bg-white transition-transform ${
              mobileMenuOpen ? "rotate-45 translate-y-2" : ""
            }`}
          />
          <span
            className={`w-6 h-0.5 bg-white transition-opacity ${
              mobileMenuOpen ? "opacity-0" : ""
            }`}
          />
          <span
            className={`w-6 h-0.5 bg-white transition-transform ${
              mobileMenuOpen ? "-rotate-45 -translate-y-2" : ""
            }`}
          />
                  </button>
      </nav>

      {/* Mobile Menu - Slide-out Panel (below xl) */}
                  <div
        className={`fixed inset-0 z-[999] flex justify-end bg-black/50 backdrop-blur-lg transition-all duration-300 xl:hidden ${
          mobileMenuOpen 
            ? "opacity-100 visible" 
            : "opacity-0 invisible pointer-events-none"
                    }`}
        onClick={closeMobileMenu}
                  >
        <div
          className={`w-full sm:w-[400px] h-full px-5 py-5 bg-[#f24d2b] overflow-y-auto pb-10 transition-transform duration-300 ${
            mobileMenuOpen ? "translate-x-0" : "translate-x-full"
                              }`}
          onClick={(e) => e.stopPropagation()}
                            >
          {/* Panel Header */}
          <div className="flex items-center justify-between gap-4">
            <Logo variant="white" />
            <button
              className="flex flex-col items-center justify-center w-10 h-10 gap-1.5"
              onClick={closeMobileMenu}
              aria-label="Close menu"
          >
              <span className="w-6 h-0.5 bg-white rotate-45 translate-y-2" />
              <span className="w-6 h-0.5 bg-white opacity-0" />
              <span className="w-6 h-0.5 bg-white -rotate-45 -translate-y-2" />
            </button>
          </div>

          {/* User Type Switcher - Mobile (pill style) */}
          <div className="flex items-center gap-1 bg-white/15 rounded-full p-[3px] mt-6 mb-6 md:mt-8 md:mb-8">
            {userTypes.map((type) => (
              <Link
                key={type.id}
                to={type.href}
                onClick={closeMobileMenu}
                className={`flex-1 text-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-full text-[12px] sm:text-[13px] md:text-[14px] font-medium transition-all duration-200 ${
                  currentUserType === type.id
                    ? "bg-white text-[#122d63] shadow-sm"
                    : "text-white/80 hover:text-white"
                }`}
              >
                {type.label}
              </Link>
            ))}
      </div>

          {/* Navigation Links - Mobile */}
          <div className="flex flex-col gap-1 mb-6">
            {navLinks.map((item) => {
              const active = isNavActive(item);
              const isExpanded = mobileExpanded === item.title;
              
              return (
                <div key={item.title}>
                  <button
                    className={`flex items-center justify-between w-full gap-2 px-3 py-2.5 rounded-lg transition-colors ${
                      active ? "bg-white/15" : ""
                    }`}
                    onClick={() => toggleMobileExpanded(item.title)}
                  >
                    <span className="text-[14px] sm:text-sm md:text-base text-white font-medium">
                      {item.title}
                    </span>
                    <img
                      src={arrowDownIcon}
                      alt=""
                      className={`w-6 h-6 sm:w-8 sm:h-8 transition-transform duration-200 ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isExpanded
                        ? "grid-rows-[1fr] opacity-100 mt-1"
                        : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="flex flex-col gap-1 pl-3">
                        {item.links.map((link) => {
                          const childActive = location.pathname === link.href;
                          return (
                            <Link
                              key={link.href}
                              to={link.href}
                              onClick={closeMobileMenu}
                              className={`text-[13px] sm:text-xs md:text-sm leading-tight px-3 py-2 rounded-lg transition-colors ${
                                childActive
                                  ? "bg-white/20 text-white font-semibold"
                                  : "text-white/80 hover:text-white hover:bg-white/10"
                              }`}
                            >
                              {link.title}
                            </Link>
  );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Login Button - Mobile */}
          <Link
            to="/login"
            onClick={closeMobileMenu}
            className="block w-full py-2.5 sm:py-3 px-6 bg-[#f8f8f8] rounded-[40px] text-[13px] sm:text-sm text-[#0a0e2e] text-center font-medium"
          >
            Login
          </Link>
        </div>
      </div>
    </header>
  );
};

export default PartnerHeader;
